import { Box, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ReferenceLine,
	ResponsiveContainer,
	AreaChart,
	Area,
	LineChart,
	Line,
} from "recharts";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

export default function LineChartComponent({
	maxValuebps = 0,
	minValuebps = 0,
	data = [],
	customTooltip = null,
	yOptions = {},
	xOptions = {},
	gridOptions = {},
	areaChartOptions = {},
	areaOptions = {},
	mainColor = null,
	mainLineColor = null,
	referenceLines = [], //{name,value,opt}
	lastTickRef = 0.95,
	objectColorsLine = false,
	stackedGraphColors = ["orange", "green", "blue", "red", "yellow"],
	stackedGraphKeys = [],
}) {
	const [refWidth, setRefWidth] = useState(null);
	const M_STYLE = 300;
	// Estilização
	const containerRef = useRef(null);
	const [maxTicks, setMaxTicks] = useState(10);
	const theme = useTheme();
	const MAIN_COLOR = mainColor || theme.palette.cardGraphic.graphColor.blue;
	const MAIN_LINE_COLOR =
		mainLineColor || theme.palette.cardGraphic.graphLineColor.blue;

	// Define dinamicamente o intervalo para os ticks do eixo X
	const calcInterval = () => {
		if (!maxTicks || maxTicks < 1 || data.length < 2 || data.length < maxTicks)
			return undefined;
		const effectiveMaxTicks = Math.max(2, maxTicks);
		const adjustedLength = Math.floor(data.length * lastTickRef);
		return Math.max(1, Math.floor(adjustedLength / effectiveMaxTicks));
	};

	useEffect(() => {
		const updateTicks = () => {
			if (containerRef.current) {
				const { width } = containerRef.current.getBoundingClientRect();
				setRefWidth(width);
				const calculatedTicks = Math.floor(width / 80);
				setMaxTicks(Math.max(2, calculatedTicks));
			}
		};

		// Medir no início
		updateTicks();

		// Atualizar ao redimensionar
		window.addEventListener("resize", updateTicks);
		return () => window.removeEventListener("resize", updateTicks);
	}, []);

	const mergedYOptions = {
		tickFormatter: formatBytes,
		interval: 0,
		domain: [minValuebps, maxValuebps],
		tick: {
			fill:
				theme.palette.mode === "dark"
					? theme.palette.white.main
					: theme.palette.black.main,
			opacity: 0.7,
		},
		style: { fontSize: "10px", fontWeight: 500 },
		...yOptions,
	};

	const mergedXOptions = {
		dataKey: "clock",
		tickFormatter: (clock, index) =>
			dayjs.unix(clock).tz(timeZone).format("DD/MM"),
		style: { fontSize: "10px", fontWeight: 500, marginRight: "50px" },
		tick: {
			fill:
				theme.palette.mode === "dark"
					? theme.palette.white.main
					: theme.palette.black.main,
			opacity: 0.7,
		},
		...xOptions,
	};

	const mergedGridOptions = {
		stroke: MAIN_LINE_COLOR,
		strokeOpacity: 0.5,
		vertical: false,
		strokeDasharray: "3",
		...gridOptions,
	};

	const mergedAreaChartOptions = {
		margin: {
			top: 20,
			right: 30,
			left: 10,
			bottom: 0,
			...(refWidth <= M_STYLE && {
				top: 8,
				right: 10,
				left: 4,
			}),
		},
		...areaChartOptions,
	};

	const mergedAreaOptions = {
		type: "monotone",
		dataKey: "value",
		name: "Tráfego",
		strokeWidth: 1,
		stroke: MAIN_COLOR,
		fill: `deafult-gradient-${MAIN_COLOR}`,
		fillOpacity: 1,
		stackId: 1,
		dot: false,
		...areaOptions,
	};

	const mergedLineOptions = {
		dot: false,
		type: "monotone",
		...areaOptions,
	};

	// Tootip padrão
	const DefaultToolTip = ({ active, payload, label }) => {
		const theme = useTheme();
		const lines = 3; // Número máximo de itens a serem exibidos

		if (active && payload && payload?.length) {
			// Ordena e separa os itens para exibição
			const visibleItems = [...payload]
				?.reverse()
				?.slice(0, lines < payload?.length ? lines - 1 : lines);
			const otherItems = [...payload]
				?.reverse()
				?.slice(lines < payload?.length ? lines - 1 : lines);
			const otherSum = otherItems?.reduce((sum, item) => sum + item?.value, 0);

			return (
				<Box
					sx={{
						bgcolor: theme.palette.paletteCharts.lineCharts,
						padding: "8px",
						borderRadius: "8px",
						boxShadow: `0px 2px 4px ${theme.palette.colors.black.darker}33`,
					}}
				>
					<Typography fontSize={"14px"} fontWeight={500} mb={1}>
						{`Data: ${dayjs.unix(label).tz(timeZone).format("DD/MM HH:mm")}`}
					</Typography>

					{/* Itens visíveis */}
					{visibleItems?.map((item) => (
						<Stack
							key={item?.dataKey}
							direction={"row"}
							alignItems={"center"}
							gap={0.5}
						>
							<Box
								sx={{
									bgcolor: item?.color || MAIN_COLOR,
									width: "12px",
									height: "12px",
									borderRadius: "2px",
									border: `1px solid ${item?.color || MAIN_LINE_COLOR}`,
								}}
							/>
							<Typography fontSize={"14px"}>
								{`${
									payload.length > 1
										? item?.dataKey?.match(/\[(.*?)\]/)?.[1] || item?.dataKey
										: "Tráfego"
								}: ${formatBytes(item?.value)}`}
							</Typography>
						</Stack>
					))}

					{/* Linha "Outros" se houver itens excedentes */}
					{otherItems?.length > 0 && (
						<Stack direction={"row"} alignItems={"center"}>
							<Box
								sx={{
									bgcolor: MAIN_COLOR,
									width: "12px",
									height: "12px",
									borderRadius: "2px",
									border: `1px solid ${MAIN_COLOR}`,
								}}
							/>
							<Typography fontSize={"14px"}>
								{`Outros: ${formatBytes(otherSum)}`}
							</Typography>
						</Stack>
					)}

					{/* Total se houver múltiplos itens */}
					{payload?.length > 1 && (
						<Typography fontSize={"14px"} mt={1} fontWeight={500}>
							{`Total: ${formatBytes(
								payload?.reduce((sum, item) => sum + item?.value, 0)
							)}`}
						</Typography>
					)}
				</Box>
			);
		}
		return null;
	};

	return (
		<ResponsiveContainer ref={containerRef} width="100%" height="100%">
			{objectColorsLine ? (
				<LineChart data={data} margin={{ top: 10, left: -20, bottom: 0 }}>
					{customTooltip ? (
						<Tooltip content={customTooltip} />
					) : (
						<Tooltip
							content={DefaultToolTip}
							allowEscapeViewBox={{ x: true, y: true }}
						/>
					)}
					<CartesianGrid {...mergedGridOptions} />
					<XAxis interval={calcInterval()} {...mergedXOptions} />
					<YAxis {...mergedYOptions} />
					<Tooltip formatter={(value) => `${value}%`} />
					{Object.entries(objectColorsLine).map(([element, color]) => (
						<Line
							key={element}
							dataKey={element}
							stroke={color}
							{...mergedLineOptions}
						/>
					))}
				</LineChart>
			) : (
				<AreaChart data={data} {...mergedAreaChartOptions}>
					<defs>
						<linearGradient
							id={`deafult-gradient-${MAIN_COLOR}`}
							x1="0"
							y1="0"
							x2="0"
							y2="1"
						>
							<stop
								offset="60%"
								stopColor={MAIN_LINE_COLOR}
								stopOpacity={0.5}
							/>
							<stop
								offset="100%"
								stopColor={MAIN_COLOR}
								stopOpacity={theme.palette.mode == "dark" ? 0 : 0.2}
							/>
						</linearGradient>
					</defs>
					{Object.keys(data[0] || {})
						.filter((key) =>
							stackedGraphKeys.length > 0
								? key !== "clock" &&
								  stackedGraphKeys.some((k) => key.includes(k))
								: key !== "clock"
						)
						.map((key, idx) => (
							<defs>
								<linearGradient
									id={`deafult-gradient-${idx}`}
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop
										offset="60%"
										stopColor={
											theme?.palette?.cardGraphic?.graphLineColor?.[
												stackedGraphColors[idx % stackedGraphColors.length]
											]
										}
										stopOpacity={0.5}
									/>
									<stop
										offset="100%"
										stopColor={
											theme?.palette?.cardGraphic?.graphColor?.[
												stackedGraphColors[idx % stackedGraphColors.length]
											]
										}
										stopOpacity={theme.palette.mode == "dark" ? 0 : 0.2}
									/>
								</linearGradient>
							</defs>
						))}
					<CartesianGrid {...mergedGridOptions} />
					<XAxis interval={calcInterval()} {...mergedXOptions} />
					<YAxis {...mergedYOptions} />
					{customTooltip ? (
						<Tooltip content={customTooltip} />
					) : (
						<Tooltip content={DefaultToolTip} />
					)}
					{Array.isArray(data) &&
						(Object.keys(data?.[0] || {}).length < 3 || data?.[0]?.value ? (
							<Area
								{...mergedAreaOptions}
								fill={`url(#deafult-gradient-${MAIN_COLOR})`}
							/>
						) : (
							Object.keys(data[0] || {})
								.filter((key) =>
									stackedGraphKeys.length > 0
										? key !== "clock" &&
										  stackedGraphKeys.some((k) => key.includes(k))
										: key !== "clock"
								)
								.map((key, idx) => (
									<Area
										key={key}
										type="monotone"
										dataKey={key}
										stackId="1"
										stroke={
											theme?.palette?.cardGraphic?.graphLineColor?.[
												stackedGraphColors[idx % stackedGraphColors.length]
											]
										}
										fill={`url(#deafult-gradient-${idx})`}
										fillOpacity={0.6}
									/>
								))
						))}
					{referenceLines?.map((line, index) => (
						<ReferenceLine
							key={`ref-line-${index}`}
							stroke={line.opt?.stroke || "red"}
							strokeWidth={line.opt?.strokeWidth || 1.5}
							y={line.value}
							label={{
								value: line.name,
								fill: line.opt?.label?.fill || "white",
								fontSize: line.opt?.label?.fontSize || "12px",
								fontWeight: line.opt?.label?.fontWeight || 500,
								position: line.opt?.label?.position || "insideRight",
								color: line.opt?.label?.color || theme.palette.primary.main,
								...line.opt?.label,
							}}
							{...line.opt}
						/>
					))}
				</AreaChart>
			)}
		</ResponsiveContainer>
	);
}

// Conversão de dados
const formatBytes = (bps) => {
	if (bps === 0) return "0 bps";
	const k = 1000;
	const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
	// Força i a ser no mínimo 0
	const i = Math.max(0, Math.floor(Math.log(bps) / Math.log(k)));
	const value = bps / Math.pow(k, i);
	return `${value.toFixed(2)} ${sizes[i]}`;
};
